import { inject, Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, Observable, throwError } from 'rxjs';
import { TripData } from 'src/app/shared/model/tripData';
import { TravelBoardData } from 'src/app/shared/model/travel-board-data';
import { TranslocoService } from '@jsverse/transloco';
import { ConfirmationService } from 'primeng/api';
import { Router } from '@angular/router';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TripService } from 'src/app/trip-calculator/services/trip.service';
import { EcoScoreService } from 'src/app/shared/services/eco-score.service';
import { CountryService } from 'src/app/trip-calculator/services/country.service';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { PackLimitationsService } from 'src/app/shared/services/pack-limitations.service';
import { LimitedFeatures } from 'src/app/shared/models/pack.models';


@Injectable({
  providedIn: 'root',
})
export class TravelBoardService {
  private apiUrl = `${environment.apiUrl}/trips`;
  private tripData: TripData[] = []; // raw data retrieve from API
  public data = signal<TravelBoardData[]>([]); // refined data shared among component
  public loading = signal<boolean>(true); // loading state for initial fetch

  // Filter signals
  public searchText = signal<string>('');
  public isDraftFilter = signal<'all' | 'draft' | 'notDraft'>('all');
  public showOnlyNeedsVerification = signal<boolean>(false);
  public showOnlyWithErrors = signal<boolean>(false);

  // Pagination signals (synced with URL query params)
  public paginationPage = signal<number>(0);
  public paginationRows = signal<number>(12);

  // Transfer dialog state
  public showTransferDialog = signal<boolean>(false);
  public transferTripId = signal<string | null>(null);
  public transferTripTitle = signal<string | null>(null);

  // Trips shown in the "Trip list" section. getTrips() no longer fetches the
  // trips created through the external API, so this filter is only a safeguard.
  public listedTrips = computed(() =>
    this.data().filter((trip) => trip.createdVia !== 'api'),
  );

  // Computed filtered data
  public filteredData = computed(() => {
    let filtered = this.listedTrips();

    // Text filter
    const search = this.searchText().trim().toLowerCase();
    if (search) {
      filtered = filtered.filter(
        (trip) =>
          (trip.title?.toLowerCase().includes(search) ?? false) ||
          (trip.countryName?.toLowerCase().includes(search) ?? false) ||
          (trip.clientName?.toLowerCase().includes(search) ?? false) ||
          (trip.companyClients?.some((client: string) =>
            client.toLowerCase().includes(search),
          ) ??
            false) ||
          (trip.fileName?.toLowerCase().includes(search) ?? false),
      );
    }

    // Draft filter
    const draftFilter = this.isDraftFilter();
    if (draftFilter === 'draft') {
      filtered = filtered.filter((trip) => trip.isDraft);
    } else if (draftFilter === 'notDraft') {
      filtered = filtered.filter((trip) => !trip.isDraft);
    }

    // AI verification filter (AI-generated trips not yet reviewed by a human)
    if (this.showOnlyNeedsVerification()) {
      filtered = filtered.filter((trip) => trip.needsVerification);
    }

    // Validation error filter
    if (this.showOnlyWithErrors()) {
      filtered = filtered.filter((trip) => trip.hasValidationErrors);
    }

    return filtered;
  });

  // Reset pagination to first page (called when filters change)
  public resetPagination(): void {
    this.paginationPage.set(0);
  }

  tripService = inject(TripService);
  ecoScoreService = inject(EcoScoreService);
  countryService = inject(CountryService);
  currentUserService = inject(CurrentUserService);
  packLimitationsService = inject(PackLimitationsService);

  constructor(
    private http: HttpClient,
    private translocoService: TranslocoService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private notificationService: NotificationService,
  ) {}

  // TODO : When it works, clean this service and check mutliple deletes functions.
  getTrips() {
    this.loading.set(true);
    // No createdVia: the API then only returns the trips created in this app
    // ("screen"). Newest first, as the API defaults to oldest first.
    const params = { orderByDate: 'desc' };
    this.http.get<{ tripData: TripData[] }>(this.apiUrl, { params }).subscribe({
      next: (response) => {
        this.tripData = response.tripData;
        const travelBoardData = this.formatData(this.tripData);
        this.data.set(travelBoardData);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error Fetching trip:', error);
        this.loading.set(false);
        throwError(() => error);
      },
    });
  }

  sendDeleteTripRequest(tripId: string) {
    return this.http.delete<void>(`${environment.apiUrl}/trips/${tripId}`).pipe(
      catchError((error) => {
        console.error('Error deleting trip:', error);
        return throwError(() => error);
      }),
    );
  }

  sendDeleteMultipleTripsRequest(tripIds: string[]) {
    return this.http
      .delete<void>(`${environment.apiUrl}/trips/`, { body: { tripIds } })
      .pipe(
        catchError((error) => {
          console.error('Error deleting multiple trips:', error);
          console.error('Error status:', error.status);
          console.error('Error message:', error.message);
          return throwError(() => error);
        }),
      );
  }

  reloadData() {
    this.data.set(this.formatData(this.tripData));
  }

  clearData() {
    this.tripData = [];
    this.data.set([]);
  }

  private formatData(tripData: TripData[]): TravelBoardData[] {
    const travelBoardData = tripData.map((tripData) => {
      return {
        id: tripData.id ?? '',
        title: tripData.title ?? '',
        countryName: tripData.country
          ? this.countryService.getCountryLabelByLang(tripData.country)
          : '',
        clientName: tripData.companyClient?.name,
        companyClients: tripData.companyClients?.map((clients) => clients.name),
        imagePath: tripData.imagePath,
        days: tripData.days ?? 0,
        travellers: tripData.travellers ?? 0,
        createdAt: tripData.createdAt,
        totalImpact:
          tripData.tripImpact && tripData.tripImpact
            ? tripData.tripImpact.totalImpact
            : 0,
        impactPerTraveller:
          tripData.tripImpact && tripData.tripImpact
            ? tripData.tripImpact.impactPerTraveller
            : 0,
        impactPerTravellerPerDay:
          tripData.tripImpact && tripData.tripImpact
            ? tripData.tripImpact.impactPerTraveller / (tripData.days || 1)
            : 0,
        // ECO SCORE CALCULATION
        ecoScore:
          tripData.tripImpact && tripData.tripImpact
            ? this.ecoScoreService.getEcoScore(
                tripData.tripImpact.carbonIntensity,
              )
            : undefined,
        isDraft: tripData.status === 'draft',
        hasValidationErrors: tripData.validationStatus === 'errors',
        isAiGenerated: tripData.isAiGenerated ?? false,
        isHumanVerified: tripData.isHumanVerified ?? false,
        needsVerification:
          (tripData.isAiGenerated ?? false) && !(tripData.isHumanVerified ?? false),
        fileName: tripData.fileName,
        createdVia: tripData.createdVia ?? 'screen',
      };
    });
    return travelBoardData;
  }

  confirmDelete(tripId: string) {
    const message = this.translocoService.translate('travelBoards.deleteTrip');
    this.confirmationService.confirm({
      message: message,
      acceptLabel: this.translocoService.translate('yes'),
      rejectLabel: this.translocoService.translate('no'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.deleteTrip(tripId),
    });
  }

  deleteTrip(tripId: string) {
    const messageTripsDeletSuccessfully = this.translocoService.translate(
      'travelBoards.tripSuccessDeleted',
    );
    const messageTripsDeletError = this.translocoService.translate(
      'travelBoards.tripsDeleteFailed',
    );
    this.sendDeleteTripRequest(tripId).subscribe({
      next: () => {
        this.data.update((trips) => trips.filter((t) => t.id !== tripId));
        this.notificationService.success(messageTripsDeletSuccessfully);
        this.currentUserService.refreshCurrentUser();
      },
      error: (error) => {
        console.error('Error deleting trip:', error);
        this.notificationService.error(messageTripsDeletError);
        // this.loadTrips();
      },
    });
  }

  viewTrip(tripId: string) {
    this.router.navigate(['/home/trips/' + tripId + '/impact']);
  }

  editTrip(tripId: string) {
    this.tripService.navigateToTripDashboard(tripId);
  }

  deleteMultipleTrips(tripIds: string[]) {
    if (!tripIds || tripIds.length === 0) return;
    const messageTripsDeletSuccessfully = this.translocoService.translate(
      'travelBoards.tripSuccessDeleted',
    );
    const messageTripsDeletError = this.translocoService.translate(
      'travelBoards.tripsDeleteFailed',
    );
    this.confirmationService.confirm({
      message: this.translocoService.translate('travelBoards.deleteTrips'),
      acceptLabel: this.translocoService.translate('yes'),
      rejectLabel: this.translocoService.translate('no'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.sendDeleteMultipleTripsRequest(tripIds).subscribe({
          next: () => {
            this.data.update((trips) =>
              trips.filter((t) => t.id && !tripIds.includes(t.id)),
            );
            this.notificationService.success(messageTripsDeletSuccessfully);
            this.currentUserService.refreshCurrentUser();
          },
          error: (error) => {
            console.error('Error deleting trip:', error);
            if (
              error.status === 409 &&
              error.error?.code === 'TRIPS_HAVE_CONTRIBUTIONS'
            ) {
              this.notificationService.error(
                this.translocoService.translate(
                  'travelBoards.tripsHaveContributions',
                ),
              );
            } else {
              this.notificationService.error(messageTripsDeletError);
            }
          },
        });
      },
    });
  }
  duplicate(id: string) {
    this.tripService.duplicateTrip(id).subscribe({
      next: () => {
        this.currentUserService.refreshCurrentUser();
      },
      error: (error) => {
        console.error('Error duplicating trip:', error);
      },
    });
  }

  recalculateTrips(tripIds: string[]) {
    if (!tripIds || tripIds.length === 0) return;
    this.http
      .put<{ message: string }>(`${environment.apiUrl}/trips/recalculate`, { tripIds })
      .subscribe({
        next: () => {
          this.notificationService.success(
            this.translocoService.translate('travelBoards.recalculateSuccess'),
          );
          this.getTrips();
        },
        error: (error) => {
          console.error('Error recalculating trips:', error);
          this.notificationService.error(
            this.translocoService.translate('travelBoards.recalculateFailed'),
          );
        },
      });
  }

  recalculateTrip(tripId: string) {
    this.recalculateTrips([tripId]);
  }

  /**
   * Mark (or unmark) trips as reviewed by a human. Clears the "needs verification"
   * badge on AI-generated trips. Works for a single trip or a batch.
   */
  markVerified(tripIds: string[], isHumanVerified = true) {
    if (!tripIds || tripIds.length === 0) return;
    this.http
      .put<{ message: string; count: number }>(`${environment.apiUrl}/trips/verify`, {
        tripIds,
        isHumanVerified,
      })
      .subscribe({
        next: () => {
          this.data.update((trips) =>
            trips.map((t) =>
              t.id && tripIds.includes(t.id)
                ? {
                    ...t,
                    isHumanVerified,
                    needsVerification: isHumanVerified ? false : t.isAiGenerated,
                  }
                : t,
            ),
          );
          this.notificationService.success(
            this.translocoService.translate('travelBoards.markVerifiedSuccess'),
          );
        },
        error: (error) => {
          console.error('Error verifying trips:', error);
          this.notificationService.error(
            this.translocoService.translate('travelBoards.markVerifiedFailed'),
          );
        },
      });
  }

  // Transfer trip methods (super admin only)
  openTransferDialog(trip: TravelBoardData) {
    this.transferTripId.set(trip.id!);
    this.transferTripTitle.set(
      trip.title ||
        this.translocoService.translate('travelBoards.untitledTrip'),
    );
    this.showTransferDialog.set(true);
  }

  closeTransferDialog() {
    this.showTransferDialog.set(false);
    this.transferTripId.set(null);
    this.transferTripTitle.set(null);
  }

  sendTransferTripRequest(
    tripId: string,
    targetCompanyId: string,
  ): Observable<{ success: boolean; message: string }> {
    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(`${environment.apiUrl}/trips/${tripId}/transfer`, { targetCompanyId })
      .pipe(
        catchError((error) => {
          console.error('Error transferring trip:', error);
          return throwError(() => error);
        }),
      );
  }

  transferTrip(targetCompanyId: string) {
    const tripId = this.transferTripId();
    if (!tripId) return;

    const successMessage = this.translocoService.translate(
      'travelBoards.tripTransferred',
    );
    const errorMessage = this.translocoService.translate(
      'travelBoards.transferFailed',
    );

    this.sendTransferTripRequest(tripId, targetCompanyId).subscribe({
      next: () => {
        // Remove the trip from the local data (it now belongs to another company)
        this.data.update((trips) => trips.filter((t) => t.id !== tripId));
        this.notificationService.success(successMessage);
        this.closeTransferDialog();
      },
      error: (error) => {
        console.error('Error transferring trip:', error);
        this.notificationService.error(errorMessage);
        this.closeTransferDialog();
      },
    });
  }

  getActions(trip: TravelBoardData) {
    const isSuperAdmin = this.currentUserService.isSuperAdmin();

    return [
      {
        icon: 'pi pi-chart-pie',
        label: this.translocoService.translate('carbonFootprint'),
        action: () => this.viewTrip(trip.id!),
        testId: 'view-trip-button',
        hidden: trip.isDraft,
      },
      {
        icon: 'pi pi-pencil',
        label: this.translocoService.translate('edit'),
        action: () => this.editTrip(trip.id!),
        testId: 'edit-trip-button',
      },
      {
        icon: 'pi pi-trash',
        label: this.translocoService.translate('delete'),
        action: () => this.confirmDelete(trip.id!),
        testId: 'delete-trip-button',
      },
      {
        icon: 'pi pi-sync',
        label: this.translocoService.translate('travelBoards.recalculate'),
        action: () => this.recalculateTrip(trip.id!),
        testId: 'recalculate-trip-button',
        hidden: trip.isDraft,
      },
      {
        icon: 'pi pi-check-circle',
        label: this.translocoService.translate('travelBoards.markAsVerified'),
        action: () => this.markVerified([trip.id!]),
        testId: 'verify-trip-button',
        hidden: !trip.needsVerification,
      },
      {
        icon: this.packLimitationsService.canCreateTrip()
          ? 'pi pi-clone'
          : 'pi pi-lock',
        label: this.translocoService.translate('duplicate'),
        action: () =>
          this.packLimitationsService.canCreateTrip()
            ? this.duplicate(trip.id!)
            : this.packLimitationsService.openModal(LimitedFeatures.Trip),
        testId: 'duplicate-trip-button',
      },
      {
        icon: 'pi pi-arrow-right-arrow-left',
        label: this.translocoService.translate('travelBoards.transfer'),
        action: () => this.openTransferDialog(trip),
        testId: 'transfer-trip-button',
        hidden: !isSuperAdmin,
      },
    ];
  }
}
